/**
 * Riimukurssi – yksi suomenkielinen lähde kaikille riimuille.
 * Sävy: peili, ei ennustusta.
 */

/** Map rune slug to filename in client/public/rune-stones (updated rune images). */
const RUNE_IMAGE_BY_SLUG = {
  fehu: 'Fehu-rune-riimut.svg',
  uruz: 'Uriz-rune-riimut.svg',
  thurisaz: 'Thurisaz-rune-riimut.svg',
  ansuz: 'Ansuz-rune-riimut.svg',
  raidho: 'Raido-rune-riimut.svg',
  kenaz: 'Kaunaz-rune-riimut.svg',
  gebo: 'Gebo-rune-riimut.svg',
  wunjo: 'Wunjo-rune-riimut.svg',
  hagalaz: 'Hagalaz-rune-riimut.svg',
  naudhiz: 'Nautiz-rune-riimut.svg',
  isa: 'Isa-rune-riimut.svg',
  jera: 'Jera-rune-riimut.svg',
  eihwaz: 'Eihwaz-rune-riimut.svg',
  perthro: 'Perthro-rune-riimut.svg',
  algiz: 'Algiz-rune-riimut.svg',
  sowilo: 'Sowelu-rune-riimut.svg',
  tiwaz: 'Teiwaz-rune-riimut.svg',
  berkano: 'Berkana-rune-riimut.svg',
  ehwaz: 'Ehwaz-rune-riimut.svg',
  mannaz: 'Mannaz-rune-riimut.svg',
  laguz: 'Laguz-rune-riimut.svg',
  ingwaz: 'Inguz-rune-riimut.svg',
  dagaz: 'Dagaz-rune-riimut.svg',
  othala: 'Othala-rune-riimut.svg',
};

const runeImagePath = (slug) => `/rune-stones/${RUNE_IMAGE_BY_SLUG[slug] || ''}`;

/** Lähteet: Energiakeskus (symboliikka, riimuperheet), Thuleia (merkitykset, historia). */
export const COURSE_SOURCES = [
  { title: 'Energiakeskus – Riimujen symboliikka', url: 'https://energiakeskus.com/energiasivut/symboliikka/riimut/' },
  { title: 'Thuleia – Riimujen merkitykset', url: 'https://www.thuleia.com/riimujenmerkitykset.html' },
  { title: 'Thuleia – Riimujen historia', url: 'https://www.thuleia.com/riimuhistoria.html' },
];

/** Lähteet kurssisivun "Lähteet"-osioon (Wikipedia + muut). */
export const WIKIPEDIA_SOURCES = [
  { title: 'Riimukirjoitus (fi.wikipedia.org)', url: 'https://fi.wikipedia.org/wiki/Riimukirjoitus' },
  { title: 'Runes (en.wikipedia.org)', url: 'https://en.wikipedia.org/wiki/Runes' },
  { title: 'Runic inscriptions (en.wikipedia.org)', url: 'https://en.wikipedia.org/wiki/Runic_inscriptions' },
  { title: 'Elder Futhark (en.wikipedia.org)', url: 'https://en.wikipedia.org/wiki/Elder_Futhark' },
];

/** Deep-dive -sivujen vähimmäislähteet (jokaisella riimulla). */
export const DEEP_DIVE_WIKI_SOURCES = [
  { title: 'Runes (en.wikipedia.org)', url: 'https://en.wikipedia.org/wiki/Runes' },
  { title: 'Riimukirjoitus (fi.wikipedia.org)', url: 'https://fi.wikipedia.org/wiki/Riimukirjoitus' },
];

export const runes = [
  {
    slug: 'fehu',
    nameFi: 'Fehu',
    phonetic: 'F',
    coreFi: 'Varallisuus ja hedelmällisyus – mitä sinulla jo on, kasvaa kun huomioit sen.',
    symbolismFi: 'Fehu viittaa karjaan ja siihen, mitä ruokkii. Se peilaa omaa suhtettasi resursseihin, turvallisuuteen ja antamiseen. Ei ennusta rahaa vaan kysyy: mitä arvostat jo nyt?',
    interpretationHelpFi: 'Kun Fehu tulee vastaan, pysähdy: mitä sinulla on jo runsaasti? Anna itsellesi lupa nauttia siitä. Yksi pieni teko, joka kunnioittaa omaa "karjaasi" (aika, taito, läheiset).',
    keywordsFi: ['varallisuus', 'hedelmällisyus', 'antaminen', 'turvallisuus', 'arvo'],
    journalPromptsFi: [
      'Mitä minulla on jo runsaasti, jota en huomaa?',
      'Miten voin jakaa jotain tänään ilman että vähennän itseäni?',
      'Mikä antaa minulle turvallisuuden tunteen?',
      'Mitä "karjaa" ruokin – ja kunnioitanko sitä?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä kolme kertaa hitaasti.',
      'Sano mielessäsi: "Näen sen, mitä minulla jo on."',
      'Tee yksi pieni teko: anna jotain (ajatus, kiitos, apu) ilman odotusta vastakaikua.',
    ],
    historyUseFi: 'Riimuja käytettiin historiallisesti sekä kirjoitusmerkkeinä että symbolina. Fehu liittyi jaettavaan hyvinvointiin; karja oli varallisuuden mittari.',
    whenItRepeatsFi: ['Sama riimu toistuu, kun elämässä painottuvat resurssit tai turvallisuus.', 'Tulee vastaan, kun olet jakamassa tai vastaanottamassa.', 'Kysyt usein: mitä minulla on jo?'],
    shadowPitfallsFi: ['Materialismi: keskittyminen vain aineelliseen.', 'Antamisen unohtaminen: kaikki itsellä.', 'Vertaaminen: toisten "sato" näyttää suuremmalta.'],
    sources: COURSE_SOURCES,
    aett: 1,
    teemaFi: ['Kasvu', 'Yhteys'],
    themesFi: ['Kasvu'],
    image: runeImagePath('fehu'),
  },
  {
    slug: 'uruz',
    nameFi: 'Uruz',
    phonetic: 'U',
    coreFi: 'Voima ja terveys – luonnonvoima sinussa, ei pakotetta.',
    symbolismFi: 'Uruz yhdistää villiintyneeseen ja terveeseen elinvoimaan. Se peilaa omaa voimaa ja sitoutumista muutokseen. Ei ennusta vaan muistuttaa: voima on jo olemassa.',
    interpretationHelpFi: 'Kun Uruz ilmestyy, kysy: missä tarvitsen rohkeutta? Ei tarvitse rynnätä – yksi askel riittää. Keho ja mieli ovat liittolaisia.',
    keywordsFi: ['voima', 'terveys', 'rohkeus', 'muutos', 'luonto'],
    journalPromptsFi: [
      'Mistä tunnen voimaa tällä hetkellä?',
      'Mikä yksi asia vaatisi rohkeutta – ja miten voin ottaa yhden askeleen?',
      'Miten voin kunnioittaa kehoani tänään?',
      'Mitä "villiä" minussa on, jota en päästä esiin?',
    ],
    miniRitualFi: [
      'Asetu johonkin paikkaan ja tunne jalkojesi kosketus lattiaan.',
      'Hengitä syvään ja sano mielessäsi: "Voimani on täällä."',
      'Tee yksi pieni liike (venyttely, kävelylenkki) tietoisesti.',
    ],
    historyUseFi: 'Uruz viittasi alkukantaiseen voimaan ja terveyteen. Sitä käytettiin symbolina muutokselle ja sisäiselle voimalle.',
    whenItRepeatsFi: ['Rohkeus tai muutos on keskiössä.', 'Keho tai jaksaminen vaatii huomiota.', 'Villi potentiaali haluaa tulla näkyväksi.'],
    shadowPitfallsFi: ['Voiman pakottaminen: liian kova tempo.', 'Muutoksen pelko: jähmettyminen.', 'Oman kehon väheksyminen.'],
    sources: COURSE_SOURCES,
    aett: 1,
    teemaFi: ['Muutos', 'Kasvu'],
    themesFi: ['Kasvu'],
    image: runeImagePath('uruz'),
  },
  {
    slug: 'thurisaz',
    nameFi: 'Thurisaz',
    phonetic: 'Th',
    coreFi: 'Torjunta ja kynnys – suojaus ja selkeytys, ei hyökkäys.',
    symbolismFi: 'Thurisaz liittyy kynnyskiviin ja suojaukseen. Se peilaa rajoja ja sitä, mitä pitää torjua tai päästää sisään. Ei ennusta vihaa vaan rajaa.',
    interpretationHelpFi: 'Kun Thurisaz tulee, kysy: mitä minun pitää sanoa ei? Tai: mikä ovi on auki, jota minun kannattaa käyttää? Aseta yksi selkeä raja.',
    keywordsFi: ['raja', 'suojaus', 'kynnys', 'selkeytys', 'torjunta'],
    journalPromptsFi: [
      'Mihin rajaan minun pitäisi sanoa "ei" tällä viikolla?',
      'Mikä asia tarvitsee selkeyttä eikä lisää tunteita?',
      'Ketä tai mitä suojelen – ja onko se terveellistä?',
      'Mitä "ovesta" minun kannattaa astua sisään?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä kolme kertaa.',
      'Sano mielessäsi: "Rajanani ovat selkeät."',
      'Kirjoita yksi asia, jolle sanot "ei" tänään (vaikka vain mielessä).',
    ],
    historyUseFi: 'Thurisaz liitettiin kynnyskiviin ja suojaukseen. Sitä käytettiin rajojen ja torjunnan symbolina.',
    whenItRepeatsFi: ['Rajat tai konfliktit toistuvat.', 'Tunnet tarpeen suojata itseäsi tai tilaa.', 'Jokin ovi haluaa joko sulkea tai avata.'],
    shadowPitfallsFi: ['Aggressio: raja muuttuu hyökkäukseksi.', 'Piiloutuminen: liikaa "ei" kaikelle.', 'Valta vs. suojelu – sekoitus.'],
    sources: COURSE_SOURCES,
    aett: 1,
    teemaFi: ['Suoja', 'Rajat'],
    themesFi: ['Rajat'],
    image: runeImagePath('thurisaz'),
  },
  {
    slug: 'ansuz',
    nameFi: 'Ansuz',
    phonetic: 'A',
    coreFi: 'Viesti ja viisaus – sana ja kuuleminen.',
    symbolismFi: 'Ansuz yhdistää jumalaan, viestiin ja viisauteen. Se peilaa kommunikaatiota: mitä sanot ja mitä kuulet. Ei ennusta vaan kysyy: kenen ääntä kuuntelet?',
    interpretationHelpFi: 'Kun Ansuz tulee, kuuntele: kuka tai mikä viesti toistuu? Kirjoita yksi lause, jonka tarvitset kuulla tai sanoa.',
    keywordsFi: ['viesti', 'viisaus', 'kommunikaatio', 'inspiraatio', 'opettaja'],
    journalPromptsFi: [
      'Mikä viesti on toistunut minulle viime aikoina?',
      'Kenelle minun pitäisi sanoa jotain tänään?',
      'Mitä "opettajaa" minussa tai ympärilläni en kuuntele?',
      'Mikä yksi lause tarvitsen kuulla – voinko sanoa sen itselleni?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä rauhallisesti.',
      'Sano ääneen yksi lause, jonka haluat kuulla (esim. "Olet riittävä.").',
      'Kuuntele hetki hiljaisuutta sen jälkeen.',
    ],
    historyUseFi: 'Ansuz yhdistettiin viestiin, jumalaan ja viisauteen. Sitä käytettiin kommunikaation ja sisäisen äänen symbolina.',
    whenItRepeatsFi: ['Sama viesti tai opettaja toistuu.', 'Tarvitset sanoa tai kuulla jotain tärkeää.', 'Inspiraatio tai oivallus on lähellä.'],
    shadowPitfallsFi: ['Pelkkä puhuminen ilman kuuntelua.', 'Ulkoisen validaation etsiminen.', 'Oman äänen hiljentäminen.'],
    sources: COURSE_SOURCES,
    aett: 1,
    teemaFi: ['Selkeys', 'Yhteys'],
    themesFi: ['Yhteys'],
    image: runeImagePath('ansuz'),
  },
  {
    slug: 'raidho',
    nameFi: 'Raidho',
    phonetic: 'R',
    coreFi: 'Matka ja rytmi – liike ja järjestys.',
    symbolismFi: 'Raidho viittaa matkaan ja rytmiin. Se peilaa sitä, miten liikut elämässä – kiire vai tasapaino. Ei ennusta reissuja vaan kysyy: onko rytmisi terve?',
    interpretationHelpFi: 'Kun Raidho ilmestyy, tarkkaile: oletko liian paikallaan vai liian levoton? Yksi pieni muutos rytmiin (tauko tai askel) riittää.',
    keywordsFi: ['matka', 'rytmi', 'liike', 'järjestys', 'tasapaino'],
    journalPromptsFi: [
      'Mikä on minun nykyinen "rytmini" – kiire vai jumitus?',
      'Mihin minun pitäisi antaa aikaa tänä viikkona?',
      'Mikä yksi asia tarvitsee järjestämistä?',
      'Miltä "matka" tuntuu nyt – ja mitä haluan muuttaa?',
    ],
    miniRitualFi: [
      'Kävele lyhyt lenkki (vaikka huoneesta toiseen) tietoisesti.',
      'Hengitä askelta tahdissa: sisään 2, ulos 2.',
      'Sano mielessäsi: "Liikkeeni on oikeaan tahtiin."',
    ],
    historyUseFi: 'Raidho viittasi matkaan ja rytmiin. Sitä käytettiin liikkeen ja järjestyksen symbolina.',
    whenItRepeatsFi: ['Matka, muutto tai suunnanvaihdos on ajankohtainen.', 'Rytmi tai rutiinit tarvitsevat tarkastelua.', 'Ohjaus: kuka pitää ohjaksia?'],
    shadowPitfallsFi: ['Liika kiire: ei pysähdystä.', 'Jumitus: pelko liikkeestä.', 'Toisten reittien vertaileminen.'],
    sources: COURSE_SOURCES,
    aett: 1,
    teemaFi: ['Muutos', 'Yhteys'],
    themesFi: ['Selkeys'],
    image: runeImagePath('raidho'),
  },
  {
    slug: 'kenaz',
    nameFi: 'Kenaz',
    phonetic: 'K',
    coreFi: 'Tuli ja tieto – valo, taito ja ymmärrys.',
    symbolismFi: 'Kenaz liittyy tuleen ja tietoon. Se peilaa omaa taitoa ja intoa oppia. Ei ennusta onnea vaan kysyy: mitä haluat valaista?',
    interpretationHelpFi: 'Kun Kenaz tulee, kysy: mikä asia tarvitsee "valoa" – eli rehellistä katsantaa? Tai: mikä taitosi haluaa tulla näkyväksi?',
    keywordsFi: ['valo', 'tieto', 'taito', 'innostus', 'luovuus'],
    journalPromptsFi: [
      'Mitä asiaa minun kannattaa katsoa suoraan silmiin?',
      'Mikä taitoni on piilossa ja haluaa esiin?',
      'Mikä "tuli" minussa on sammunut – voinko sytyttää sen uudelleen?',
      'Mitä haluan oppia seuraavaksi?',
    ],
    miniRitualFi: [
      'Sytytä kynttilä tai katso valoa (lamppu, ikkuna).',
      'Sano mielessäsi: "Annan valon tälle asialle."',
      'Kirjoita yksi lause: mitä nyt näen selkeämmin?',
    ],
    historyUseFi: 'Kenaz liitettiin tuleen, valoon ja tietoon. Sitä käytettiin oivalluksen ja taidon symbolina.',
    whenItRepeatsFi: ['Jokin asia haluaa valoa tai rehellistä katsantaa.', 'Luovuus tai taito hakee ulosventtiiliä.', 'Uusi alku tai rohkeus on käsillä.'],
    shadowPitfallsFi: ['Valon pakottaminen: liian kova kritiikki.', 'Taidon piilottaminen nöyryytenä.', 'Tulen sammuttaminen pelolla.'],
    sources: COURSE_SOURCES,
    aett: 1,
    teemaFi: ['Selkeys', 'Kasvu'],
    themesFi: ['Selkeys'],
    image: runeImagePath('kenaz'),
  },
  {
    slug: 'gebo',
    nameFi: 'Gebo',
    phonetic: 'G',
    coreFi: 'Lahja ja vaihtokauppa – antaminen ja vastaanottaminen.',
    symbolismFi: 'Gebo on lahjan riimu. Se peilaa tasapainoa antamisen ja vastaanottamisen välillä. Ei ennusta rakkautta vaan kysyy: annatko itsellesi luvan vastaanottaa?',
    interpretationHelpFi: 'Kun Gebo tulee, tarkista: oletko enemmän antaja vai vastaanottaja? Tasapaino ei ole 50/50 joka päivä – mutta pitkällä tähtäimellä kunnioita molempia.',
    keywordsFi: ['lahja', 'vaihtokauppa', 'antaminen', 'vastaanottaminen', 'kumppanuus'],
    journalPromptsFi: [
      'Annanko itselleni luvan vastaanottaa apua tai ylistystä?',
      'Kenelle haluan antaa jotain tänään – ilman odotusta?',
      'Mikä "vaihtokauppa" elämässäni on epätasapainossa?',
      'Mitä lahjaa en ole ottanut vastaan?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä kolme kertaa.',
      'Sano mielessäsi: "Annan ja vastaanotan tasapainoisesti."',
      'Tee yksi pieni lahja itsellesi (aika, lause, teko).',
    ],
    historyUseFi: 'Gebo oli lahjan ja vaihtokaupan riimu. Sitä käytettiin antamisen ja vastaanottamisen tasapainon symbolina.',
    whenItRepeatsFi: ['Antaminen tai vastaanottaminen on epätasapainossa.', 'Kumppanuus tai sitoumus tarvitsee huomiota.', 'Lahja – annatko tai otatko luvan?'],
    shadowPitfallsFi: ['Pelkkä antaminen ilman vastaanottamista.', 'Vastaanottamisen kieltäytyminen.', 'Lahjan sidominen odotuksiin.'],
    sources: COURSE_SOURCES,
    aett: 1,
    teemaFi: ['Yhteys', 'Rajat'],
    themesFi: ['Yhteys'],
    image: runeImagePath('gebo'),
  },
  {
    slug: 'wunjo',
    nameFi: 'Wunjo',
    phonetic: 'W',
    coreFi: 'Ilo ja yhteenkuuluvuus – onni, joka tulee yhteydestä.',
    symbolismFi: 'Wunjo viittaa iloon ja yhteenkuuluvuuteen. Se peilaa sitä, missä tunnet kuuluvasi ja onnelliseksi. Ei ennusta onnea vaan muistuttaa: ilo on usein yhteistä.',
    interpretationHelpFi: 'Kun Wunjo ilmestyy, kysy: kenen tai minkä kanssa tunnet yhteyttä? Tai: mikä pieni asia tuotti iloa viime viikolla? Anna sille tilaa.',
    keywordsFi: ['ilo', 'yhteenkuuluvuus', 'harmonia', 'yhteys', 'onnellisuus'],
    journalPromptsFi: [
      'Mistä tunnen iloa tällä hetkellä?',
      'Kenen kanssa haluan viettää aikaa tänä viikkona?',
      'Mikä asia tuo harmoniaa kotiin tai arkeen?',
      'Miten voin jakaa iloa jonkun kanssa tänään?',
    ],
    miniRitualFi: [
      'Muista yksi hetki, jolloin tunshit iloa – pysähdy siihen mielessä.',
      'Hengitä syvään ja sano: "Ilo on osa minua."',
      'Lähetä yksi viesti tai kiitos jollekin.',
    ],
    historyUseFi: 'Wunjo viittasi iloon ja yhteenkuuluvuuteen. Sitä käytettiin harmonian ja oman paikan löytämisen symbolina.',
    whenItRepeatsFi: ['Ilo tai yhteys on keskiössä.', 'Oma paikka – missä tunnet kuuluvasi?', 'Riemu ilman painolastia.'],
    shadowPitfallsFi: ['Ilon minimoiminen "ei pitäisi".', 'Yhteenkuuluvuuden etsiminen ulkoa vain.', 'Huolien antaminen painaa iloa.'],
    sources: COURSE_SOURCES,
    aett: 1,
    teemaFi: ['Yhteys'],
    themesFi: [],
    image: runeImagePath('wunjo'),
  },
  {
    slug: 'hagalaz',
    nameFi: 'Hagalaz',
    phonetic: 'H',
    coreFi: 'Luonnonvoima ja este – myrsky, josta selvitään.',
    symbolismFi: 'Hagalaz liittyy rakeisiin ja luonnonvoimaan. Se peilaa esteitä ja muutoksia, joita et voi hallita. Ei ennusta onnettomuuksia vaan muistuttaa: myrskyn jälkeen on selkeyttä.',
    interpretationHelpFi: 'Kun Hagalaz tulee, älä taistele myrskyä vastaan. Kysy: mitä voin hyväksyä tässä tilanteessa? Yksi pieni turvallinen askel eteenpäin.',
    keywordsFi: ['este', 'muutos', 'luonnonvoima', 'selviytyminen', 'hyväksyminen'],
    journalPromptsFi: [
      'Mikä "myrsky" on ollut tai on – mitä siitä olen oppinut?',
      'Mitä en voi muuttaa ja mitä voin hyväksyä?',
      'Mistä olen selvinnyt – miten voin kunnioittaa sitä?',
      'Mikä pieni askel vie minut myrskyn jälkeen?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä hitaasti.',
      'Sano mielessäsi: "Hyväksyn sen, mitä en voi muuttaa."',
      'Tee yksi pieni asia, joka tuo turvallisuutta (järjestä, soita, kirjoita).',
    ],
    historyUseFi: 'Hagalaz liitettiin rakeisiin ja luonnonvoimaan. Sitä käytettiin välttämättömän muutoksen ja rakenteen symbolina.',
    whenItRepeatsFi: ['Myrsky tai este on ohitettava.', 'Jokin rakennelma hajoaa – sisäistä tai ulkoista.', 'Selviytyminen ja hyväksyminen.'],
    shadowPitfallsFi: ['Taistelu myrskyä vastaan turhaan.', 'Uhriutuminen: "kaikki on pilalla".', 'Muutoksen kieltäminen.'],
    sources: COURSE_SOURCES,
    aett: 2,
    teemaFi: ['Muutos'],
    image: runeImagePath('hagalaz'),
  },
  {
    slug: 'naudhiz',
    nameFi: 'Naudhiz',
    phonetic: 'N',
    coreFi: 'Tarve ja pakko – tarpeet ohjaavat, ei määrää.',
    symbolismFi: 'Naudhiz viittaa tarpeeseen ja ahdinkoon. Se peilaa sitä, mitä sinulla on pakko tehdä tai käsitellä. Ei ennusta köyhyyttä vaan kysyy: mikä tarve on nyt tärkein?',
    interpretationHelpFi: 'Kun Naudhiz ilmestyy, tunnista yksi konkreettinen tarve (ei halu). Mitä yksi asia voisi helpottaa? Pyydä apua tai tee pieni askel itse.',
    keywordsFi: ['tarve', 'pakko', 'ahdinko', 'kestävyys', 'apupyyntö'],
    journalPromptsFi: [
      'Mikä on minun tärkein tarve tällä hetkellä?',
      'Mistä en halua puhua – ja kenen kanssa voisin?',
      'Mitä "pakkoa" olen kääntänyt voimaksi aiemmin?',
      'Keneltä voisin pyytää apua tänään?',
    ],
    miniRitualFi: [
      'Hengitä kolme kertaa syvään.',
      'Sano mielessäsi: "Tunnistan tarpeeni ja teen yhden askeleen."',
      'Kirjoita yksi asia, josta voisit pyytää apua (vaikka et vielä pyydä).',
    ],
    historyUseFi: 'Naudhiz viittasi tarpeeseen ja ahdinkoon. Sitä käytettiin perustarpeiden ja kestävyyden symbolina.',
    whenItRepeatsFi: ['Perustarpeet tulevat esiin.', 'Apupyyntö tai kestävyys on ajankohtainen.', 'Kitka tai "pakko" toistuu.'],
    shadowPitfallsFi: ['Tarpeen kieltäminen jääräpäisesti.', 'Apupyynnön häpeä.', 'Liika sinnikkyys ilman tukea.'],
    sources: COURSE_SOURCES,
    aett: 2,
    teemaFi: ['Rajat'],
    themesFi: ['Muutos'],
    image: runeImagePath('naudhiz'),
  },
  {
    slug: 'isa',
    nameFi: 'Isa',
    phonetic: 'I',
    coreFi: 'Jää ja pysähtyminen – tauko ennen uutta liikettä.',
    symbolismFi: 'Isa on jään riimu. Se peilaa pysähtymistä ja jähmeyttä. Ei ennusta kylmyyttä vaan muistuttaa: joskus paras teko on olla tekemättä.',
    interpretationHelpFi: 'Kun Isa tulee, älä pakota liikettä. Kysy: mitä tapahtuisi, jos en tekisi mitään tänään? Anna itsellesi lupa levätä.',
    keywordsFi: ['jää', 'pysähtyminen', 'tauko', 'selkeytys', 'kärsivällisyys'],
    journalPromptsFi: [
      'Mihin minun pitäisi pysähtyä tällä viikolla?',
      'Mikä asia tarvitsee "jäätämistä" – eli ei uusia päätöksiä?',
      'Miten voin antaa itselleni luvan olla tekemättä?',
      'Mitä selkeytyy, kun en kiirehdi?',
    ],
    miniRitualFi: [
      'Istu tai makaa 1 minuutti liikkumatta.',
      'Hengitä vain – älä suunnittele.',
      'Sano mielessäsi: "Tauko on sallittu."',
    ],
    historyUseFi: 'Isa liitettiin jäähän ja pysähtymiseen. Sitä käytettiin tauon ja selkeyden symbolina.',
    whenItRepeatsFi: ['Tauko tai pysähtyminen on tarpeen.', 'Liian paljon liikettä – keho tai mieli pyytää levätä.', 'Selkeytys vaatii ettei tee uusia päätöksiä.'],
    shadowPitfallsFi: ['Liian pitkä jähmettyminen.', 'Tauon kieltäminen.', 'Pelkkä odottaminen ilman reflektiota.'],
    sources: COURSE_SOURCES,
    aett: 2,
    teemaFi: ['Rajat', 'Selkeys'],
    themesFi: ['Suoja'],
    image: runeImagePath('isa'),
  },
  {
    slug: 'jera',
    nameFi: 'Jera',
    phonetic: 'J',
    coreFi: 'Vuodenkierto ja sadonkorjaus – aika ja palkka.',
    symbolismFi: 'Jera viittaa vuodenkiertoon ja sadonkorjaukseen. Se peilaa syklejä: mitä olet kylvänyt, nyt korjataan. Ei ennusta vuotta vaan kysyy: mitä olet jo kylvänyt?',
    interpretationHelpFi: 'Kun Jera tulee, muista: jotkut asiat tarvitsevat aikaa. Mikä "sato" on kypsymässä? Anna sille aikaa tai tunnusta jo korjattu sato.',
    keywordsFi: ['sykli', 'aika', 'sadonkorjaus', 'palkka', 'kärsivällisyys'],
    journalPromptsFi: [
      'Mitä olen "kylvänyt" viime kuukausina – mikä kypsyy nyt?',
      'Mikä asia tarvitsee vielä aikaa ennen kuin näen tuloksen?',
      'Mitä "satoa" olen jo korjannut ja voin juhlia?',
      'Miten voin kunnioittaa omaa rytmiäni eikä muiden odotuksia?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä.',
      'Sano mielessäsi: "Annan ajan tehdä työnsä."',
      'Nimeä yksi asia, jonka "sadon" näet jo – kiitä siitä.',
    ],
    historyUseFi: 'Jera viittasi vuodenkiertoon ja sadonkorjaukseen. Sitä käytettiin syklin ja oikean ajan symbolina.',
    whenItRepeatsFi: ['Sato kypsyy tai on korjattavissa.', 'Aika vaatii kärsivällisyyttä.', 'Menneet teot näkyvät nyt.'],
    shadowPitfallsFi: ['Sadon repiminen liian aikaisin.', 'Ajan kiistäminen: "pitäisi jo tapahtua".', 'Toisten syklien vertaileminen.'],
    sources: COURSE_SOURCES,
    aett: 2,
    teemaFi: ['Kasvu'],
    themesFi: ['Muutos'],
    image: runeImagePath('jera'),
  },
  {
    slug: 'eihwaz',
    nameFi: 'Eihwaz',
    phonetic: 'Ei',
    coreFi: 'Puun ja yhdistämisen riimu – tuki ja läpimurto.',
    symbolismFi: 'Eihwaz yhdistää puuhun ja joustavuuteen. Se peilaa tukea ja sitä, mikä yhdistää eri puolia. Ei ennusta kuolemaa vaan muutosta ja tukirakennetta.',
    interpretationHelpFi: 'Kun Eihwaz ilmestyy, kysy: mikä on minun tukeni nyt? Tai: mikä yhdistää minun eri puoleni? Löydä yksi tukipiste.',
    keywordsFi: ['tuki', 'yhdistäminen', 'joustavuus', 'läpimurto', 'luottamus'],
    journalPromptsFi: [
      'Mikä tai kuka on minun tukeni tällä hetkellä?',
      'Mitä "yhdistävää" minussa on – vastakkainasettelun sijaan?',
      'Mihin voin nojata, kun asiat tuntuvat epävarmoilta?',
      'Mikä pieni läpimurto on jo tapahtunut?',
    ],
    miniRitualFi: [
      'Tunne jalkojesi tai selkäsi tuki (tuoli, lattia).',
      'Hengitä ja sano: "Minulla on tukea."',
      'Kirjoita yksi nimi tai asia, johon voit nojata.',
    ],
    historyUseFi: 'Eihwaz yhdistettiin puuhun ja joustavuuteen. Sitä käytettiin tukirakenteen ja läpimurron symbolina.',
    whenItRepeatsFi: ['Tuki tai luottamus on ajankohtainen.', 'Jokin yhdistää eri puolia.', 'Läpimurto vaatii joustoa.'],
    shadowPitfallsFi: ['Liika jäykkyys: ei taipumista.', 'Tuen kieltäminen itsenäisyyden varjolla.', 'Läpimurron pakottaminen.'],
    sources: COURSE_SOURCES,
    aett: 2,
    teemaFi: ['Suoja', 'Yhteys'],
    themesFi: ['Suoja'],
    image: runeImagePath('eihwaz'),
  },
  {
    slug: 'perthro',
    nameFi: 'Perthro',
    phonetic: 'P',
    coreFi: 'Salaus ja arpa – salattu mahdollisuus.',
    symbolismFi: 'Perthro liittyy arpaan ja salattuihin asioihin. Se peilaa sitä, mitä ei vielä näy – mahdollisuudet. Ei ennusta onnea vaan muistuttaa: jotain on tulossa esiin.',
    interpretationHelpFi: 'Kun Perthro tulee, älä yritä pakottaa avointa. Kysy: mikä asia haluaa tulla näkyväksi? Anna sille tilaa – ei pakota.',
    keywordsFi: ['salaus', 'mahdollisuus', 'yllätys', 'esiintulo', 'usko'],
    journalPromptsFi: [
      'Mikä asia on vielä "piilossa" minussa tai elämässäni?',
      'Mihin voin luottaa vaikka en näe tulosta?',
      'Mikä yllätys on tuonut minulle hyvää aiemmin?',
      'Miten voin antaa tilaa sille, mitä ei vielä näy?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä.',
      'Sano mielessäsi: "Annan tilaa sille, mitä ei vielä näy."',
      'Tee yksi pieni asia "uskon" varassa – ilman varmuutta.',
    ],
    historyUseFi: 'Perthro liitettiin salattuihin asioihin ja arpaan. Sitä käytettiin esiintulon ja mahdollisuuden symbolina.',
    whenItRepeatsFi: ['Jokin haluaa tulla näkyväksi.', 'Kohtuus ja määrämitta tarvitsevat huomion.', 'Salattu tai yllättävä elementti.'],
    shadowPitfallsFi: ['Avointa pakottaminen.', 'Liika mystifiointi.', 'Oman vastuun siirtäminen "kohtalolle".'],
    sources: COURSE_SOURCES,
    aett: 2,
    teemaFi: ['Muutos', 'Selkeys'],
    themesFi: [],
    image: runeImagePath('perthro'),
  },
  {
    slug: 'algiz',
    nameFi: 'Algiz',
    phonetic: 'Z',
    coreFi: 'Suoja ja instinkti – turva ja vaisto.',
    symbolismFi: 'Algiz on suojan riimu. Se peilaa vaistoa ja turvallisuutta. Ei ennusta pelastajaa vaan muistuttaa: sinulla on oma kyky suojautua ja tuntea turvalliseksi.',
    interpretationHelpFi: 'Kun Algiz ilmestyy, kysy: missä tarvitsen rajaa tai turvaa? Tai: mitä vaistani sanoo? Kuuntele kehoa ja tunnetta.',
    keywordsFi: ['suoja', 'vaisto', 'turvallisuus', 'raja', 'suunta'],
    journalPromptsFi: [
      'Mistä tunnen turvallisuutta tällä hetkellä?',
      'Mikä vaistani on kertonut viime aikoina?',
      'Mihin minun pitäisi sanoa "ei" suojatakseni itseäni?',
      'Miten voin luoda pienen "turvavyöhykkeen" arjessa?',
    ],
    miniRitualFi: [
      'Laita käsi sydämelle tai vatsalle.',
      'Hengitä syvään ja sano: "Olen turvassa."',
      'Kuuntele: mitä keho sanoo? Yksi lause.',
    ],
    historyUseFi: 'Algiz liitettiin suojaukseen ja vaistoon. Sitä käytettiin turvallisuuden ja rajan symbolina.',
    whenItRepeatsFi: ['Suoja tai raja tarvitsee huomiota.', 'Vaisto tai keho viestii.', 'Turvallisuus tai suunta.'],
    shadowPitfallsFi: ['Liika suojautuminen: ei avautumista.', 'Rajan unohtaminen.', 'Pelon sekoittaminen vaistoon.'],
    sources: COURSE_SOURCES,
    aett: 2,
    teemaFi: ['Suoja'],
    themesFi: ['Suoja'],
    image: runeImagePath('algiz'),
  },
  {
    slug: 'sowilo',
    nameFi: 'Sowilo',
    phonetic: 'S',
    coreFi: 'Aurinko ja voitto – valo ja selkeytys.',
    symbolismFi: 'Sowilo viittaa aurinkoon ja voittoon. Se peilaa sitä, missä olet voittaja tai missä valo näkyy. Ei ennusta menestystä vaan kysyy: missä olet jo voittanut?',
    interpretationHelpFi: 'Kun Sowilo tulee, muista: mikä asia on jo selkeä? Tai: mitä olet jo saavuttanut? Anna valon osua siihen.',
    keywordsFi: ['valo', 'voitto', 'selkeytys', 'terveys', 'tarkoitus'],
    journalPromptsFi: [
      'Mistä tunnen ylpeyttä viime aikoina?',
      'Mikä asia on "voittanut" pimeyden minussa?',
      'Miten voin antaa itselleni luvan olla onnellinen?',
      'Mikä "aurinko" minussa haluaa paistaa?',
    ],
    miniRitualFi: [
      'Katso valoa (aurinko, lamppu) hetki.',
      'Sano mielessäsi: "Annan valon osua minuun."',
      'Nimeä yksi asia, jossa olet jo "voittaja".',
    ],
    historyUseFi: 'Sowilo viittasi aurinkoon ja voittoon. Sitä käytettiin valon ja selkeyden symbolina.',
    whenItRepeatsFi: ['Voitto tai selkeytys on lähellä.', 'Valo paistaa – missä?', 'Terveys tai tarkoitus.'],
    shadowPitfallsFi: ['Valon varastaminen toisilta.', 'Liian kova itselleen.', 'Menestyksen määrittely ulkoisesti vain.'],
    sources: COURSE_SOURCES,
    aett: 2,
    teemaFi: ['Selkeys', 'Kasvu'],
    themesFi: ['Selkeys'],
    image: runeImagePath('sowilo'),
  },
  {
    slug: 'tiwaz',
    nameFi: 'Tiwaz',
    phonetic: 'T',
    coreFi: 'Oikeus ja urheus – oikeudenmukaisuus ja rohkeus.',
    symbolismFi: 'Tiwaz yhdistää oikeuteen ja urheuteen. Se peilaa sitä, mitä pidät oikeana ja miten uskallat toimia. Ei ennusta oikeudenkäyntejä vaan omaa rehellisyyttä.',
    interpretationHelpFi: 'Kun Tiwaz ilmestyy, kysy: mitä minun pitäisi tehdä oikeudenmukaisuuden vuoksi? Tai: missä tarvitsen rohkeutta? Yksi rehellinen askel.',
    keywordsFi: ['oikeus', 'urheus', 'rehellisyys', 'päämäärä', 'rohkeus'],
    journalPromptsFi: [
      'Mikä asia vaatisi minulta rehellisyyttä tänään?',
      'Missä olen ollut urhea viime aikoina?',
      'Mitä "oikeaa" minun pitäisi tehdä, vaikka se on vaikeaa?',
      'Mikä päämäärä ohjaa minua – onko se vielä aito?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä.',
      'Sano mielessäsi: "Toimin rehellisesti ja rohkeasti."',
      'Päätä yksi pieni teko, joka on oikeudenmukainen (itsellesi tai toiselle).',
    ],
    historyUseFi: 'Tiwaz yhdistettiin oikeuteen ja urheuteen. Sitä käytettiin rehellisyyden ja rohkeuden symbolina.',
    whenItRepeatsFi: ['Oikeus tai rehellisyys vaatii tekoa.', 'Rohkeus – missä tarvitset sitä?', 'Päämäärä ja oikea toiminta.'],
    shadowPitfallsFi: ['Oikeudenmukaisuus muuttuu jäykkyydeksi.', 'Urheus ilman myötätuntoa.', 'Toisten tuomitseminen.'],
    sources: COURSE_SOURCES,
    image: runeImagePath('tiwaz'),
  },
  {
    slug: 'berkano',
    nameFi: 'Berkano',
    phonetic: 'B',
    coreFi: 'Koivu ja hoito – kasvu ja hellä hoito.',
    symbolismFi: 'Berkano on koivun ja hoidon riimu. Se peilaa kasvua ja sitä, mitä vaalit. Ei ennusta raskautta vaan kysyy: mitä hoidat tai mitä haluat kasvattaa?',
    interpretationHelpFi: 'Kun Berkano tulee, kysy: mitä tai ketä hoidan? Tai: mitä haluan kasvattaa itsessäni? Yksi hellä teko itselle tai toiselle.',
    keywordsFi: ['kasvu', 'hoito', 'koivu', 'hellä', 'vaaliminen'],
    journalPromptsFi: [
      'Mitä minun pitäisi vaalia enemmän?',
      'Ketä tai mitä hoidan – onko se tasapainossa?',
      'Mikä "kasvaa" minussa nyt – annanko sille tilaa?',
      'Miten voin olla hellä itselleni tänään?',
    ],
    miniRitualFi: [
      'Laita käsi rintaan tai poskelle – hellästi.',
      'Hengitä ja sano: "Hoidan itseäni ja omaa kasvuani."',
      'Tee yksi hellä teko: itsellesi tai läheiselle.',
    ],
    historyUseFi: 'Berkano liitettiin koivuun ja hoitoon. Sitä käytettiin kasvun ja uudistumisen symbolina.',
    whenItRepeatsFi: ['Kasvu tai hoito on keskiössä.', 'Mitä tai ketä vaalit?', 'Hellä ja uudistuminen.'],
    shadowPitfallsFi: ['Hoidon unohtaminen itseltä.', 'Liika vaaliminen toista.', 'Kasvun estäminen pelolla.'],
    sources: COURSE_SOURCES,
    aett: 3,
    teemaFi: ['Kasvu'],
    themesFi: ['Kasvu'],
    image: runeImagePath('berkano'),
  },
  {
    slug: 'ehwaz',
    nameFi: 'Ehwaz',
    phonetic: 'E',
    coreFi: 'Hevonen ja yhteistyö – liike yhdessä.',
    symbolismFi: 'Ehwaz viittaa hevoseen ja yhteistyöhön. Se peilaa kumppanuutta ja luottamusta. Ei ennusta matkoja vaan kysyy: kenen kanssa liikun – ja luotammeko toisiimme?',
    interpretationHelpFi: 'Kun Ehwaz ilmestyy, tarkkaile: ketkä ovat minun "hevoseni" – luotettavat kumppanit? Tai: miten voin parantaa yhteistyötä?',
    keywordsFi: ['yhteistyö', 'luottamus', 'kumppanuus', 'liike', 'yhteys'],
    journalPromptsFi: [
      'Kenen kanssa teen parasta yhteistyötä?',
      'Miten voin vahvistaa luottamusta jossain suhteessa?',
      'Mikä "liike" tarvitsee kumppanin – kuka se voisi olla?',
      'Mitä olen oppinut yhteistyössä viime aikoina?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä.',
      'Sano mielessäsi: "Luotan yhteistyöhön."',
      'Lähetä yksi kiitos tai viesti yhteistyökumppanille.',
    ],
    historyUseFi: 'Ehwaz viittasi hevoseen ja yhteistyöhön. Sitä käytettiin luottamuksen ja kumppanuuden symbolina.',
    whenItRepeatsFi: ['Yhteistyö tai luottamus on ajankohtainen.', 'Kenen kanssa liikut – luotammeko?', 'Liike yhdessä.'],
    shadowPitfallsFi: ['Luottamuksen antaminen sokeasti.', 'Yhteistyön kieltäminen yksilöllä.', 'Kumppanin valitseminen väärin.'],
    sources: COURSE_SOURCES,
    aett: 3,
    teemaFi: ['Yhteys'],
    themesFi: ['Yhteys'],
    image: runeImagePath('ehwaz'),
  },
  {
    slug: 'mannaz',
    nameFi: 'Mannaz',
    phonetic: 'M',
    coreFi: 'Ihminen ja minä – oma identiteetti ja yhteisö.',
    symbolismFi: 'Mannaz on ihmisen riimu. Se peilaa omaa minää ja suhdetta yhteisöön. Ei ennusta muita vaan kysyy: kuka minä olen – ja kenen kanssa olen?',
    interpretationHelpFi: 'Kun Mannaz tulee, kysy: miten näen itseni? Tai: miten näen toisen? Yksi lause itsestä tai yhteisöstä – rehellisesti.',
    keywordsFi: ['ihminen', 'identiteetti', 'yhteisö', 'minä', 'toiset'],
    journalPromptsFi: [
      'Kuka minä olen tällä hetkellä – ilman rooleja?',
      'Miten suhteeni yhteisöön on muuttunut?',
      'Mitä haluan että muut näkevät minussa?',
      'Mikä "minä" tarvitsee enemmän tilaa?',
    ],
    miniRitualFi: [
      'Katso peiliin tai sulje silmät ja kuvittele oma kasvosi.',
      'Sano mielessäsi: "Olen riittävä sellaisena kuin olen."',
      'Kirjoita yksi lause itsestäsi – myötätuntoisesti.',
    ],
    historyUseFi: 'Mannaz oli ihmisen riimu. Sitä käytettiin identiteetin ja yhteisön symbolina.',
    whenItRepeatsFi: ['Kuka minä olen – ilman rooleja?', 'Yhteisö ja oma paikka.', 'Toisten ja itsen näkeminen.'],
    shadowPitfallsFi: ['Identiteetin piilotus rooleihin.', 'Vertailu: toisten "minä" parempi.', 'Yhteisön vaatiminen itseltä liikaa.'],
    sources: COURSE_SOURCES,
    aett: 3,
    teemaFi: ['Yhteys', 'Selkeys'],
    themesFi: ['Yhteys'],
    image: runeImagePath('mannaz'),
  },
  {
    slug: 'laguz',
    nameFi: 'Laguz',
    phonetic: 'L',
    coreFi: 'Vesi ja virta – tunteet ja vuo.',
    symbolismFi: 'Laguz liittyy veteen ja virtaan. Se peilaa tunteita ja sitä, miten "virrat" elämässäsi liikkuvat. Ei ennusta vesiongelmia vaan tunnevirtaa.',
    interpretationHelpFi: 'Kun Laguz ilmestyy, kysy: mitä tunnen nyt? Tai: mikä "virta" on tukkeutunut? Anna tunteille tilaa – ei tarvitse ratkaista.',
    keywordsFi: ['vesi', 'virta', 'tunteet', 'intuitio', 'vuo'],
    journalPromptsFi: [
      'Mitä tunteita en ole huomioinut viime aikoina?',
      'Mikä "virta" minussa on jumissa – miten voin päästää?',
      'Miten voin luottaa intuitiooni enemmän?',
      'Mitä vesi symboloi minulle – puhdas, samea, jäätynyt?',
    ],
    miniRitualFi: [
      'Juo lasi vettä hitaasti – tietoisesti.',
      'Sano mielessäsi: "Tunteni virtaavat."',
      'Kirjoita yksi tunne sana paperille – ei analyysiä.',
    ],
    historyUseFi: 'Laguz liitettiin veteen ja virtaan. Sitä käytettiin tunteiden ja intuitiivisen vuon symbolina.',
    whenItRepeatsFi: ['Tunteet tai intuitio vaativat huomiota.', 'Jokin "virta" on tukkeutunut.', 'Vuo ja syvyys.'],
    shadowPitfallsFi: ['Tunteiden tukahduttaminen.', 'Intuitiosta piittaamattomuus.', 'Liika analyysi – ei kuuntelua.'],
    sources: COURSE_SOURCES,
    aett: 3,
    teemaFi: ['Yhteys', 'Muutos'],
    themesFi: ['Selkeys'],
    image: runeImagePath('laguz'),
  },
  {
    slug: 'ingwaz',
    nameFi: 'Ingwaz',
    phonetic: 'Ing',
    coreFi: 'Siemen ja potentiaali – sisäinen voima.',
    symbolismFi: 'Ingwaz viittaa siementeen ja potentiaaliin. Se peilaa sitä, mitä on vielä kypsymättä. Ei ennusta lasten saamista vaan sisäistä "satoa".',
    interpretationHelpFi: 'Kun Ingwaz tulee, muista: jotain on kypsymässä. Älä repi avuksi – anna aikaa. Mikä "siemen" on jo kylvetty?',
    keywordsFi: ['siemen', 'potentiaali', 'kypsyminen', 'sisäinen voima', 'sato'],
    journalPromptsFi: [
      'Mikä asia on "kypsymässä" minussa nyt?',
      'Mitä potentiaalia en ole vielä toteuttanut?',
      'Miten voin kunnioittaa omaa kehitystäni – ei kiirehtiä?',
      'Mikä "sato" on tulossa – mitä olen kylvänyt?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä syvään.',
      'Sano mielessäsi: "Siemeni kypsyy oikeaan aikaan."',
      'Tee yksi pieni asia, joka kunnioittaa omaa kehitystäsi.',
    ],
    historyUseFi: 'Ingwaz viittasi siementeen ja potentiaaliin. Sitä käytettiin kypsymisen ja sisäisen voiman symbolina.',
    whenItRepeatsFi: ['Jokin kypsyy – anna aikaa.', 'Siemen on kylvetty.', 'Potentiaali odottaa oikeaa hetkeä.'],
    shadowPitfallsFi: ['Sadon repiminen liian aikaisin.', 'Potentiaalin kieltäminen.', 'Kiirehtiminen kehitystä.'],
    sources: COURSE_SOURCES,
    aett: 3,
    teemaFi: ['Kasvu'],
    themesFi: ['Kasvu'],
    image: runeImagePath('ingwaz'),
  },
  {
    slug: 'dagaz',
    nameFi: 'Dagaz',
    phonetic: 'D',
    coreFi: 'Päivä ja läpimurto – valoisuus ja muutos.',
    symbolismFi: 'Dagaz on päivän riimu. Se peilaa läpimurtoa ja uutta alkuun. Ei ennusta onnea vaan kysyy: mikä on jo muuttunut – tai mikä haluaa muuttua?',
    interpretationHelpFi: 'Kun Dagaz ilmestyy, tunnista: mikä "päivä" on jo sarastanut? Tai: mikä pieni läpimurto on tapahtunut? Anna sille arvo.',
    keywordsFi: ['päivä', 'läpimurto', 'muutos', 'valoisuus', 'uusi alku'],
    journalPromptsFi: [
      'Mikä asia on muuttunut minussa viime aikoina?',
      'Mikä "päivä" on sarastamassa – mitä näen nyt selkeämmin?',
      'Miten voin juhlia pientä läpimurtoa?',
      'Mikä uusi alku tarvitsee vain tunnustuksen?',
    ],
    miniRitualFi: [
      'Katso valoa (aurinko tai lamppu) hetki.',
      'Sano mielessäsi: "Päivä sarastaa – näen uuden mahdollisuuden."',
      'Nimeä yksi asia, joka on jo muuttunut – kiitä siitä.',
    ],
    historyUseFi: 'Dagaz oli päivän ja läpimurron riimu. Sitä käytettiin valoisuuden ja uuden alun symbolina.',
    whenItRepeatsFi: ['Läpimurto tai muutos on tapahtumassa.', 'Päivä sarastaa – mitä näet?', 'Uusi alku tarvitsee tunnustuksen.'],
    shadowPitfallsFi: ['Muutoksen kieltäminen.', 'Paradoksi: vain mustavalkoinen näkökulma.', 'Uuden alun viivyttely.'],
    sources: COURSE_SOURCES,
    aett: 3,
    teemaFi: ['Muutos', 'Selkeys'],
    themesFi: ['Muutos'],
    image: runeImagePath('dagaz'),
  },
  {
    slug: 'othala',
    nameFi: 'Othala',
    phonetic: 'O',
    coreFi: 'Perintö ja koti – juuret ja kuuluminen.',
    symbolismFi: 'Othala viittaa perintöön ja kotiin. Se peilaa juuria, perhettä ja sitä, mihin kuulut. Ei ennusta kiinteistöjä vaan omaa paikkaa maailmassa.',
    interpretationHelpFi: 'Kun Othala tulee, kysy: missä on minun "kotini"? Tai: mitä olen perinyt – ja mitä haluan viedä eteenpäin? Yksi juuri, jota kunnioitan.',
    keywordsFi: ['perintö', 'koti', 'juuret', 'kuuluminen', 'perhe'],
    journalPromptsFi: [
      'Mistä tunnen kuuluvani?',
      'Mitä olen perinyt perheeltäni tai yhteisöltäni?',
      'Mikä on minun "kotini" – paikka, ihminen, tuntemus?',
      'Mitä haluan viedä eteenpäin seuraavalle sukupolvelle?',
    ],
    miniRitualFi: [
      'Pysähdy ja hengitä.',
      'Sano mielessäsi: "Juureni antavat minulle tukea."',
      'Muista yksi paikka tai ihminen, josta tunnet kuuluvasi – kiitä mielessä.',
    ],
    historyUseFi: 'Othala viittasi perintöön ja kotiin. Sitä käytettiin juurien ja kuulumisen symbolina.',
    whenItRepeatsFi: ['Koti tai juuret tarvitsevat huomiota.', 'Mitä olet perinyt – mitä viedään eteenpäin?', 'Kuuluminen: paikka tai yhteisö.'],
    shadowPitfallsFi: ['Jäykät perinteet ilman uudistumista.', 'Kodin sekoittaminen kontrolliin.', 'Juurien kieltäminen.'],
    sources: COURSE_SOURCES,
    aett: 3,
    teemaFi: ['Yhteys', 'Rajat'],
    themesFi: ['Rajat'],
    image: runeImagePath('othala'),
  },
];

export const getRuneBySlug = (slug) => runes.find((r) => r.slug === slug);
export const getAllSlugs = () => runes.map((r) => r.slug);
